<script setup lang="ts">
import { ref, onUnmounted, watch } from 'vue'
import { useDateFormat } from '@vueuse/core'

const props = defineProps<{
  timerOn: boolean
  time?: string
}>()

const elapsedTime = ref('00:00:00')
const startTime = ref<number | null>(null)
const startTimeDisplay = ref<string>('')
const timerId = ref<number | null>(null)

const formatTime = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ].join(':')
}

const startTimer = () => {
  if (props.time) {
    startTime.value = new Date(props.time).getTime()
  } else {
    startTime.value = Date.now()
  }

  startTimeDisplay.value = useDateFormat(startTime.value, 'MMMM Do HH:mm[h]').value

  timerId.value = window.setInterval(() => {
    if (startTime.value) {
      const currentTime = Date.now()
      const elapsed = currentTime - startTime.value
      elapsedTime.value = formatTime(elapsed)
    }
  }, 1000)
}

const stopTimer = () => {
  if (timerId.value) {
    clearInterval(timerId.value)
    timerId.value = null
  }
  elapsedTime.value = '00:00:00'
  startTime.value = null
  startTimeDisplay.value = ''
}

watch(
  () => props.timerOn,
  (newtimerOn) => {
    if (newtimerOn) {
      startTimer()
    } else {
      stopTimer()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (timerId.value) {
    clearInterval(timerId.value)
  }
})
</script>

<template>
  <div v-if="timerOn" class="timer-container">
    <div class="timer">
      <div class="timer-label text">
        <i class="pi pi-clock"></i>
        <p>Start Time</p>
      </div>
      <p class="text">{{ startTimeDisplay }}</p>
    </div>
    <div class="timer">
      <div class="timer-label text">
        <i class="pi pi-stopwatch"></i>
        <p>Running Time</p>
      </div>
      <div class="running-time">{{ elapsedTime }}</div>
    </div>
  </div>
  <div v-else class="timer-container-off">
    <i class="pi pi-exclamation-circle"></i>
    <p>No live events at the moment.</p>
  </div>
</template>

<style scoped>
.timer-container-off {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--message-info-color, #3b82f6);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  background: var(--message-info-background, #dbeafeb2);
  padding: 10px;
  border-radius: 6px;
}
p {
  margin: 0;
}
.timer-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.timer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.text {
  color: var(--global-textSecondaryColor, #6b7280);
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.timer-label {
  display: flex;
  gap: 0.5rem;
}
.running-time {
  color: var(--input-hover-borderColor, #3b82f6);
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  line-height: normal;
  font-weight: 700;
  border-radius: 6px;
  background: var(--gray-50, #dbeafeb2);
  padding: 4px 16px;
}
</style>
